import { useEffect, useState, useRef } from 'react';
import styles from './index.module.scss';
import type { FormProps } from 'antd';
import { Dropdown, Modal, UploadFile } from 'antd';
import AddNewChildForm, { AddNewChildFormRef } from './_components/AddNewChildForm/AddNewChildForm';
import EditChildInfoForm from './_components/editChildInfoForm/EditChildInfoForm';
import { IoMdMore } from 'react-icons/io';
import { TbEdit } from 'react-icons/tb';
import emptyChildIcon from '@assets/images/Tadatoon-Profile.png';
import { childListResponse } from 'src/core/repo/child/types';
import {
  useChildInfo,
  useChildList,
  useInsertChild,
  useUpdateChild,
} from 'src/core/repo/child/useCase';
import dayjs from 'dayjs';
import { FaPlus } from 'react-icons/fa6';
import aiPlan from '@assets/images/ai4-plan.png';

export type IAddNewChildType = {
  child_id?: number;
  childName?: string;
  birthDate?: dayjs.Dayjs;
  gender?: string;
  photo?: UploadFile[];
};

const MyChild = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [childList, setChildList] = useState<childListResponse[]>([]);
  const [selectedChild, setSelectedChild] = useState<childListResponse | null>(
    null,
  );
  const addChildFormRef = useRef<AddNewChildFormRef>(null);

  const { mutateAsync: insertChild } = useInsertChild();
  const { mutateAsync: getChildList } = useChildList();
  const { mutateAsync: getChildInfo } = useChildInfo(0);
  const { mutateAsync: updateChild } = useUpdateChild();

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);

  const handleOpenEditDialog = (childId: number) => {
    getChildInfo(childId)
      .then((data: childListResponse[]) => {
        const dayjsDate = dayjs(data[0].BirthDate);
        setSelectedChild(data[0]);
        console.log(dayjsDate);

        setIsOpenEditDialog(true);
      })
      .catch(console.error);
  };
  const handleCloseEditDialog = () => setIsOpenEditDialog(false);

  const getDropdownItems = (child: childListResponse) => [
    {
      key: '1',
      label: (
        <div
          onClick={() => handleOpenEditDialog(child.ChildID)}
          className={styles.dropdownItem}>
          <p>Edit Child Information</p>
          <TbEdit size={18} color="#999" />
        </div>
      ),
    },
  ];

  const onFinish: FormProps<IAddNewChildType>['onFinish'] = (values) => {
    insertChild(
      {
        child_name: values.childName!,
        birth_date: values.birthDate!.format('YYYY-MM-DD'),
        gender_id: parseFloat(values.gender!),
        photo: values?.photo?.[0]?.thumbUrl || '',
      },
      {
        onSuccess: () => {
          getChildList({}).then((data: childListResponse[]) =>
            setChildList(data),
          );
          // Clear form values and file list after successful submission
          addChildFormRef.current?.resetForm();
        },
      },
    );
    setIsDialogOpen(false);
  };

  const onFinishFailed: FormProps<IAddNewChildType>['onFinishFailed'] = (
    errorInfo,
  ) => console.log('Failed:', errorInfo);

  const handleUpdateChild: FormProps<IAddNewChildType>['onFinish'] = (
    values,
  ) => {
    if (!selectedChild) return;
    updateChild(
      {
        child_id: selectedChild.ChildID,
        child_name: values.childName || selectedChild.ChildName,
        birth_date:
          values.birthDate!.format('YYYY-MM-DD') || selectedChild.BirthDate,
        gender_id: values.gender
          ? parseFloat(values.gender)
          : selectedChild.GenderID,
        photo: values?.photo?.[0]?.thumbUrl || selectedChild.Photo,
      },
      {
        onSuccess: () =>
          getChildList({}).then((data: childListResponse[]) =>
            setChildList(data),
          ),
      },
    );
    setIsOpenEditDialog(false);
  };

 

  useEffect(() => {
    getChildList({})
      .then((data: childListResponse[]) => setChildList(data))
      .catch(console.error);
  }, [getChildList]);

  return (
    <div className={styles.wrapper}>
      {childList.length === 0 ? (
        <>
          <div className={styles.no_child}>
            <img src={aiPlan} alt="no_child" />
            <h2>You have not added any children yet.</h2>
            <button className={styles.add_button} onClick={handleOpenDialog}>
              <FaPlus className={styles.icon} size={18} />
              <p>Add</p>
            </button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.child_list}>
            {childList.map((child) => (
              <div key={child.ChildID} className={styles.option}>
                <div className={styles.option_detail}>
                  <div className={styles.option_image}>
                    <img
                      src={child.PhotoURL ? child.PhotoURL : emptyChildIcon}
                      alt={child.ChildName}
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className={styles.option_kid_info}>
                    <p>{child.ChildAge} years old</p>
                    <h4>{child.ChildName}</h4>
                  </div>
                </div>

                <Dropdown
                  menu={{ items: getDropdownItems(child) }}
                  placement="bottomRight"
                  trigger={['click']}>
                  <button className={styles.moreButton}>
                    <IoMdMore size={24} color="#99999" />
                  </button>
                </Dropdown>
              </div>
            ))}
          </div>

          <button className={styles.add_button} onClick={handleOpenDialog}>
            <FaPlus className={styles.icon} size={18} />
            <p>Add</p>
          </button>
        </>
      )}

      <Modal
        className={styles.modal}
        title="Add New Child"
        open={isDialogOpen}
        onCancel={handleCloseDialog}
        footer={null}>
        <AddNewChildForm
          ref={addChildFormRef}
          fileList={fileList}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          setFileList={setFileList}
        />
      </Modal>

      <Modal
        key={selectedChild?.ChildID}
        className={styles.modal}
        title="Edit Child Information"
        open={isOpenEditDialog}
        onCancel={handleCloseEditDialog}
        footer={null}>
        {selectedChild && (
          <EditChildInfoForm
            fileList={fileList}
            onFinish={handleUpdateChild}
            onFinishFailed={onFinishFailed}
            setFileList={setFileList}
            selectedChild={selectedChild}
          />
        )}
      </Modal>
    </div>
  );
};

export default MyChild;
